import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2, Sparkles, Users, ChefHat, Trees, MessagesSquare, HeartHandshake, Award } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES, SITE } from "@/lib/site";

const IMAGES_NDIS_LOGO = SITE.ndisLogo;

const CAPACITY = [
  "Daily living and self-care skills",
  "Cooking, nutrition and meal planning",
  "Money management and decision-making",
  "Emotional regulation and mental capacity",
  "Social communication and relationship skills",
  "Goal setting and routine building",
];

const COMMUNITY = [
  "Group cooking classes and shared meals",
  "Outdoor recreational activities and group adventures",
  "Community access, transport and inclusion support",
  "Group counselling and peer-based learning",
  "Building confidence to participate independently",
  "Connecting with mainstream community programs",
];

export default function NdisServices() {
  return (
    <div data-testid="page-ndis-services">
      {/* HERO */}
      <section className="relative bg-warm-50 pt-24 pb-20 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.group} alt="Group support" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-r from-warm-50 via-warm-50/95 to-warm-50/70" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 lg:px-12">
          <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-6">
            NDIS Services · Registered Provider
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-ink-900 max-w-4xl">
            <em className="text-brand not-italic">20+ years</em> supporting people with disabilities to build the lives they want.
          </h1>
          <p className="mt-7 text-lg lg:text-xl leading-relaxed text-ink-500 max-w-3xl">
            Neurogility is an NDIS registered provider delivering Capacity Building and Community
            Participation services across Melbourne. Our team has spent over two decades working alongside
            people with disabilities — and we bring every hour of that experience to each plan we deliver.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contact?service=ndis"
              data-testid="ndis-hero-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-sm font-semibold hover:bg-brand-hover transition-colors"
            >
              Register your interest <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/professionals"
              data-testid="ndis-hero-refer-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-ink-900 px-7 py-4 text-sm font-semibold hover:border-brand hover:text-brand transition-colors"
            >
              Refer a participant
            </Link>
          </div>
        </div>
      </section>

      {/* EXPERIENCE BANNER */}
      <section className="py-16 lg:py-20 bg-white border-b border-warm-200/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 md:grid-cols-4 gap-10 items-center">
          <Reveal>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand mb-4">
              <Award className="w-7 h-7" />
            </div>
            <p className="font-serif text-5xl text-ink-900 leading-none">20+</p>
            <p className="mt-2 text-sm text-ink-500 uppercase tracking-widest">Years of disability experience</p>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand mb-4">
              <HeartHandshake className="w-7 h-7" />
            </div>
            <p className="font-serif text-5xl text-ink-900 leading-none">NDIS</p>
            <p className="mt-2 text-sm text-ink-500 uppercase tracking-widest">Registered provider</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand mb-4">
              <Users className="w-7 h-7" />
            </div>
            <p className="font-serif text-5xl text-ink-900 leading-none">1:6</p>
            <p className="mt-2 text-sm text-ink-500 uppercase tracking-widest">Facilitator to participant</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand mb-4">
              <Sparkles className="w-7 h-7" />
            </div>
            <p className="font-serif text-5xl text-ink-900 leading-none">ACA</p>
            <p className="mt-2 text-sm text-ink-500 uppercase tracking-widest">Registered counsellor</p>
          </Reveal>
        </div>
      </section>

      {/* CAPACITY BUILDING SERVICE */}
      <section className="py-20 lg:py-28 bg-warm-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-5">Service 01</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-ink-900">
              Capacity Building Services
            </h2>
            <p className="mt-5 text-lg text-ink-500 leading-relaxed">
              We help participants build the skills, confidence and routines they need for the life they
              want — across daily living, communication, decision-making and emotional wellbeing. Sessions
              are tailored to each plan and delivered one-on-one or in small groups.
            </p>
            <Link
              to="/contact?service=capacity-building"
              data-testid="register-capacity-building"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-sm font-semibold hover:bg-brand-hover transition-colors"
            >
              Register for Capacity Building <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-8">
            <Reveal delay={0.08}>
              <div className="bg-white border border-warm-200/60 rounded-2xl p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <ChefHat className="w-5 h-5" />
                  </div>
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <MessagesSquare className="w-5 h-5" />
                  </div>
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <Sparkles className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-xs uppercase tracking-widest text-ink-400 mb-4">What we work on</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {CAPACITY.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-ink-700">
                      <CheckCircle2 className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COMMUNITY PARTICIPATION SERVICE */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-7 order-2 lg:order-1">
            <div className="bg-warm-50 border border-warm-200/60 rounded-2xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <Users className="w-5 h-5" />
                </div>
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <Trees className="w-5 h-5" />
                </div>
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <HeartHandshake className="w-5 h-5" />
                </div>
              </div>
              <p className="text-xs uppercase tracking-widest text-ink-400 mb-4">What participants do</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {COMMUNITY.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-ink-700">
                    <CheckCircle2 className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.05} className="lg:col-span-5 lg:pl-8 order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-5">Service 02</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-ink-900">
              Community Participation Services
            </h2>
            <p className="mt-5 text-lg text-ink-500 leading-relaxed">
              We support participants to belong, contribute and connect — through group cooking classes,
              outdoor recreational activities, community access and shared experiences. The aim is real
              participation, not just attendance.
            </p>
            <Link
              to="/contact?service=community-participation"
              data-testid="register-community-participation"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-sm font-semibold hover:bg-brand-hover transition-colors"
            >
              Register for Community Participation <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* EXPERIENCE STATEMENT */}
      <section className="bg-warm-100 py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-7">Two decades of practice</p>
            <blockquote className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-ink-900 tracking-tight italic">
              "Twenty years of working with people with disabilities teaches you one thing above all —
              people grow when they feel seen, trusted, and given the right kind of support."
            </blockquote>
            <p className="mt-8 text-sm uppercase tracking-widest text-ink-400">— The Neurogility team</p>
          </Reveal>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-ink-900 text-white py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
            Ready to register or refer?
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto text-lg leading-relaxed">
            Whether you're a participant, family member or case manager — we'd love to hear from you.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact?service=ndis"
              data-testid="ndis-closing-register-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand-soft text-ink-900 px-7 py-4 text-sm font-semibold hover:bg-white transition-colors"
            >
              Register your interest <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/professionals"
              data-testid="ndis-closing-refer-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-7 py-4 text-sm font-semibold hover:border-white transition-colors"
            >
              Refer a participant <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
